import { LEDGER_SOURCE_LABEL, LEDGER_SOURCES, LEDGER_TYPES } from '@bariendo/lib/revenue-ledger';
import { Box, UnstyledButton } from '@mantine/core';
import { CSSProperties, ReactElement, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { HubTable } from '@/components/Hub/HubTable';
import { LoadFailed } from '@/components/Hub/LoadFailed';
import { ListRangeControl } from '@/components/Hub/ListRangeControl';
import { QueueFilterBar } from '@/components/Hub/QueueFilterBar';
import { Count, QueueFooter } from '@/components/Hub/QueueFooter';
import { useNow } from '@/hooks/useNow';
import { CohortTruncationNotice } from '@/pages/PatientListPage/components/CohortTruncationNotice';
import { usePatientListFilters } from '@/pages/PatientListPage/hooks/usePatientListFilters';
import { alert } from '@/services/alert';
import { downloadAttachmentBlob } from '@/services/attachments';
import { ORBIT_DISABLED, ORBIT_GRAY } from '@/theme/colors';
import { ledgerColumns } from './ledgerColumns';
import {
  buildLedgerRows,
  filterLedgerRows,
  hasPatient,
  LEDGER_FILTER_ALL,
  LEDGER_NO_PATIENT_PARAM,
  ledgerCsv,
  ledgerCsvFilename,
  ledgerDatePresets,
  LedgerFilters,
  ledgerRecognizedPresets,
  LedgerRow,
} from './ledgerRows';
import { LedgerSummary } from './LedgerSummary';
import { useLedgerRowActions } from './useLedgerRowActions';
import { useRevenueLedger } from './useRevenueLedger';

/** Revenue → Ledger (BAR-3030): one row per money movement, as the prototype draws it. */

const DEFAULT_PRESET = 'this-month';
const DEFAULT_RECOGNIZED_PRESET = 'all';
const DEFAULT_FILTERS: LedgerFilters = { type: LEDGER_FILTER_ALL, source: LEDGER_FILTER_ALL };

const TYPE_OPTIONS = [
  { value: LEDGER_FILTER_ALL, label: 'All' },
  ...LEDGER_TYPES.map((type) => ({ value: type, label: type })),
];

// The dropdown names the payors' invoices; the cells and the summary say the payor.
const SOURCE_OPTIONS = [
  { value: LEDGER_FILTER_ALL, label: 'All' },
  ...LEDGER_SOURCES.map((source) => ({
    value: source,
    label: ['CARRUM', 'TRANSCARENT', 'HOSPITAL'].includes(source)
      ? `${LEDGER_SOURCE_LABEL[source]} invoice`
      : LEDGER_SOURCE_LABEL[source],
  })),
];

const VIEW_STYLE: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };

const SEPARATOR_STYLE: CSSProperties = { width: 1, height: 18, background: 'rgba(0, 0, 0, 0.10)', margin: '0 2px' };

function chipStyle(on: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    fontSize: 12,
    lineHeight: '16px',
    padding: '5px 10px',
    borderRadius: 999,
    border: `1px solid ${on ? 'rgba(36, 55, 33, 0.35)' : 'rgba(0, 0, 0, 0.10)'}`,
    color: on ? 'var(--ev-color-green-dark, #243721)' : ORBIT_GRAY,
    background: on ? 'rgba(36, 55, 33, 0.06)' : '#fff',
    fontWeight: on ? 500 : 400,
  };
}

const BUTTON_STYLE: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
  padding: '7px 12px',
  borderRadius: 8,
  border: '1px solid rgba(0, 0, 0, 0.10)',
  background: '#fff',
  color: 'black',
  whiteSpace: 'nowrap',
};

export interface LedgerViewProps {
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

export function LedgerView({ now }: LedgerViewProps): ReactElement {
  const resolvedNow = useNow(now);
  const [searchParams, setSearchParams] = useSearchParams();
  // From the Imports history's Connect: every unconnected row, over all time.
  const [arrivedUnconnected] = useState(() => searchParams.get(LEDGER_NO_PATIENT_PARAM) === '1');
  const dateFilter = usePatientListFilters<LedgerRow>({
    resetKey: 'finance-ledger',
    chipDefinitions: [],
    defaultPreset: arrivedUnconnected ? 'all' : DEFAULT_PRESET,
    now: resolvedNow,
  });
  // Independent of the transaction date: one is cash, the other revenue (BAR-3027).
  const recognizedFilter = usePatientListFilters<LedgerRow>({
    resetKey: 'finance-ledger-recognized',
    chipDefinitions: [],
    defaultPreset: DEFAULT_RECOGNIZED_PRESET,
    now: resolvedNow,
  });
  const [filters, setFilters] = useState<LedgerFilters>(DEFAULT_FILTERS);
  const [noPatientOnly, setNoPatientOnly] = useState(arrivedUnconnected);

  // Read once on arrival; the param then goes, so a reload opens the usual view.
  useEffect(() => {
    if (!arrivedUnconnected) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(LEDGER_NO_PATIENT_PARAM);
        return next;
      },
      { replace: true }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on arrival
  }, [arrivedUnconnected]);

  const ledger = useRevenueLedger(dateFilter.range, recognizedFilter.range);

  useEffect(() => {
    if (ledger.error) {
      alert.error(`Couldn't load the ledger: ${ledger.error}`);
    }
  }, [ledger.error]);

  const { actions, withSavedNotes } = useLedgerRowActions(ledger.reload);
  const rows = useMemo(
    () => withSavedNotes(buildLedgerRows(ledger.entries, ledger.patientById)),
    [ledger.entries, ledger.patientById, withSavedNotes]
  );
  const filteredRows = useMemo(() => filterLedgerRows(rows, filters), [rows, filters]);
  const noPatientCount = useMemo(() => filteredRows.filter((row) => !hasPatient(row)).length, [filteredRows]);
  const visibleRows = useMemo(
    () => (noPatientOnly ? filteredRows.filter((row) => !hasPatient(row)) : filteredRows),
    [filteredRows, noPatientOnly]
  );

  const presets = useMemo(() => ledgerDatePresets(resolvedNow), [resolvedNow]);
  const recognizedPresets = useMemo(() => ledgerRecognizedPresets(resolvedNow), [resolvedNow]);
  const columns = useMemo(() => ledgerColumns(actions), [actions]);

  const exportCsv = (): void => {
    const url = URL.createObjectURL(new Blob([ledgerCsv(visibleRows)], { type: 'text/csv' }));
    downloadAttachmentBlob(url, ledgerCsvFilename(resolvedNow));
  };

  return (
    <Box style={VIEW_STYLE}>
      <QueueFilterBar
        date={
          <>
            <ListRangeControl
              label="Transaction date"
              presets={presets}
              presetId={dateFilter.presetId}
              value={dateFilter.range}
              onChange={dateFilter.setRange}
            />
            <ListRangeControl
              label="Recognized in"
              presets={recognizedPresets}
              presetId={recognizedFilter.presetId}
              value={recognizedFilter.range}
              onChange={recognizedFilter.setRange}
            />
          </>
        }
        filters={[
          {
            label: 'Type',
            value: filters.type,
            options: TYPE_OPTIONS,
            onChange: (type) => setFilters((current) => ({ ...current, type: type as LedgerFilters['type'] })),
          },
          {
            label: 'Source',
            value: filters.source,
            options: SOURCE_OPTIONS,
            onChange: (source) => setFilters((current) => ({ ...current, source: source as LedgerFilters['source'] })),
          },
        ]}
        extras={
          <>
            <span style={SEPARATOR_STYLE} />
            <UnstyledButton
              style={chipStyle(noPatientOnly)}
              aria-pressed={noPatientOnly}
              onClick={() => setNoPatientOnly((on) => !on)}
            >
              {`No patient · ${noPatientCount}`}
              {noPatientOnly && <span style={{ color: ORBIT_DISABLED, fontSize: 13, lineHeight: 1 }}>×</span>}
            </UnstyledButton>
          </>
        }
        actions={
          <UnstyledButton style={BUTTON_STYLE} onClick={exportCsv}>
            Export CSV
          </UnstyledButton>
        }
        isFiltered={
          filters.type !== DEFAULT_FILTERS.type ||
          filters.source !== DEFAULT_FILTERS.source ||
          noPatientOnly ||
          dateFilter.presetId !== DEFAULT_PRESET ||
          recognizedFilter.presetId !== DEFAULT_RECOGNIZED_PRESET
        }
        onReset={() => {
          setFilters(DEFAULT_FILTERS);
          setNoPatientOnly(false);
          dateFilter.setRange(DEFAULT_PRESET);
          recognizedFilter.setRange(DEFAULT_RECOGNIZED_PRESET);
        }}
      />
      {ledger.hasMore && <CohortTruncationNotice loaded={rows.length} />}
      {!ledger.error && <LedgerSummary rows={visibleRows} />}
      <HubTable
        columns={columns}
        data={visibleRows}
        loading={ledger.loading}
        initialSorting={[{ id: 'date', desc: true }]}
        emptyText="No transactions"
        emptyFallback={ledger.error && <LoadFailed what="the ledger" error={ledger.error} onRetry={ledger.reload} />}
      />
      {!ledger.error && (
        <QueueFooter
          shown={visibleRows.length}
          summary={
            <>
              <Count value={visibleRows.length} /> of <Count value={filteredRows.length} /> transactions
            </>
          }
        />
      )}
    </Box>
  );
}
